import { useCallback, useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import apiClient from "../../../../../../application/apis/tenant-management/tenant-management-client";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { ReactSelectProps } from "@graffer-inc/console-design-kit";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../../../graphql/client";
import {
  businessIssuerMaxSelectedGroupsCount,
  businessIssuerRegistrationNumberPrefix,
  businessIssuerRegistrationNumberRegex,
} from "../../../../../../application/domain/qualified-invoice/business-issuer";
import { Auth } from "../../../../../../application/domain/auth/auth";
import { useEnablePaymentHooks } from "./payment-settings";

type redirectToType = "list";

type Props = {
  orgId: string;
  auth: Auth;
};

export const useQualifiedInvoiceBusinessIssuersNewHooks = ({
  orgId,
  auth,
}: Props) => {
  const [redirectTo, setRedirectTo] = useState<redirectToType | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const [inputBusinessIssuerName, setInputBusinessIssuerName] = useState("");
  const [inputBusinessIssuerNameError, setInputBusinessIssuerNameError] =
    useState("");

  const [
    inputBusinessIssuerRegistrationNumber,
    setInputBusinessIssuerRegistrationNumber,
  ] = useState("");
  const [
    inputBusinessIssuerRegistrationNumberError,
    setInputBusinessIssuerRegistrationNumberError,
  ] = useState("");

  const [inputGroupSearchKeyword, setInputGroupSearchKeyword] = useState("");
  const [bouncedInputGroupSearchKeyword] = useDebounce(
    inputGroupSearchKeyword,
    500,
  );

  const [orgGroupSelectOptions, setOrgGroupSelectOptions] = useState<
    ReactSelectProps<string>["options"]
  >([]);

  const [inputBusinessIssuerGroups, setInputBusinessIssuerGroups] = useState<
    ReactSelectProps<string>["value"]
  >([]);
  const [inputBusinessIssuerGroupsError, setInputBusinessIssuerGroupsError] =
    useState("");

  const isValidInput =
    !inputBusinessIssuerName ||
    !inputBusinessIssuerRegistrationNumber ||
    !!inputBusinessIssuerNameError ||
    !!inputBusinessIssuerRegistrationNumberError ||
    !!inputBusinessIssuerGroupsError;

  const disabledSubmitButton = isLoading || isValidInput;

  const handleOnSetRedirectToList = useCallback(() => {
    setRedirectTo("list");
  }, []);

  const handleOnChangeBusinessIssuerName = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.value === "") {
      setInputBusinessIssuerNameError("登録事業者名を入力してください。");
    } else {
      setInputBusinessIssuerNameError("");
    }

    setInputBusinessIssuerName(e.target.value);
  };

  const handleOnChangeBusinessIssuerRegistrationNumber = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.value === "") {
      setInputBusinessIssuerRegistrationNumberError(
        "登録番号を入力してください。",
      );
    } else if (businessIssuerRegistrationNumberRegex.test(e.target.value)) {
      setInputBusinessIssuerRegistrationNumberError("");
    } else {
      setInputBusinessIssuerRegistrationNumberError(
        "登録番号は13桁の半角数字で入力してください。",
      );
    }

    setInputBusinessIssuerRegistrationNumber(e.target.value);
  };

  const handleOnChangeGroupSearchKeyword = (input: string) => {
    setInputGroupSearchKeyword(input);
  };

  const handleOnChangeBusinessIssuerGroups = (
    value: ReactSelectProps<string>["value"],
  ) => {
    if (
      (value as { value: string; label: string }[]).length >
      businessIssuerMaxSelectedGroupsCount
    ) {
      setInputBusinessIssuerGroupsError(
        `インボイスを交付可能なグループは${businessIssuerMaxSelectedGroupsCount}件まで選択可能です。`,
      );
    } else {
      setInputBusinessIssuerGroupsError("");
    }

    setInputBusinessIssuerGroups(value);
  };

  const handleOnSubmit = async () => {
    if (
      window.confirm(
        "誤った登録事業者名、登録番号を元にインボイスを交付した場合、再交付が必要となります。\n\n入力いただいた内容で登録します。よろしいですか？",
      )
    ) {
      setIsLoading(true);
      try {
        const res = await runGqlQueryV2(() =>
          gqlClient.createInvoiceBusinessIssuer(
            {
              req: {
                orgId: orgId!,
                name: inputBusinessIssuerName,
                registrationNumber:
                  businessIssuerRegistrationNumberPrefix +
                  inputBusinessIssuerRegistrationNumber,
                groupIds: (
                  inputBusinessIssuerGroups as {
                    value: string;
                    label: string;
                  }[]
                ).map((g) => g.value),
              },
            },
            gqlHeader("m"),
          ),
        );
        if (res.error) {
          throw res.error;
        }
        if (res.data?.createInvoiceBusinessIssuer) {
          toast.success("適格請求書発行事業者を登録しました。");
          handleOnSetRedirectToList();
        }
      } catch (e) {
        toast.error(
          "適格請求書発行事業者の登録に失敗しました。時間を置いて再度お試しください。",
        );
        reportUnexpectedError(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchOrgGroups = useCallback(
    async (searchKeyword: string) => {
      try {
        const { data: groups } = await apiClient.fetchGroupsByOrganizationId(
          orgId!,
          1,
          50,
          {
            name: searchKeyword,
          },
        );

        const res = await runGqlQueryV2(() =>
          gqlClient.getInvoiceBusinessIssuerGroupRelationsByOrgId(
            { orgId: orgId! },
            gqlHeader("q"),
          ),
        );
        if (res.error) {
          throw res.error;
        }
        if (res.data?.invoiceBusinessIssuerGroupRelationsByOrgID) {
          const unSelectedOrgGroups = groups.filter((g) => {
            return !res.data?.invoiceBusinessIssuerGroupRelationsByOrgID.find(
              (r) => r.groupId === g.id,
            );
          });

          const unSelectedOrgGroupOptions = unSelectedOrgGroups.map((g) => {
            return { value: g.id!, label: g.name };
          });

          setOrgGroupSelectOptions(unSelectedOrgGroupOptions);
        }
      } catch (e) {
        reportUnexpectedError(e);
        toast.error(
          "インボイスを交付可能なグループの選択肢の取得に失敗しました。時間を置いて再度お試しください。",
        );
      }
    },
    [orgId],
  );

  const { enablePayment } = useEnablePaymentHooks({
    orgId,
  });

  useEffect(() => {
    if (auth.hasPermissions("invoice_business_issuers.create")) {
      fetchOrgGroups(bouncedInputGroupSearchKeyword);
    }
  }, [auth, bouncedInputGroupSearchKeyword, fetchOrgGroups]);

  return {
    enablePayment,
    redirectTo,
    isLoading,
    disabledSubmitButton,
    inputBusinessIssuerName,
    inputBusinessIssuerNameError,
    inputBusinessIssuerRegistrationNumber,
    inputBusinessIssuerRegistrationNumberError,
    inputBusinessIssuerGroups,
    inputBusinessIssuerGroupsError,
    orgGroupSelectOptions,
    handleOnSetRedirectToList,
    handleOnChangeBusinessIssuerName,
    handleOnChangeBusinessIssuerRegistrationNumber,
    handleOnChangeGroupSearchKeyword,
    handleOnChangeBusinessIssuerGroups,
    handleOnSubmit,
  };
};
